import { damagesService } from '@/api/damages.service';
import type {
  CreateDamageDto,
  Damage,
  FindAllDamagesQueryDto,
  UpdateDamageDto,
} from '@/entities/damage.entity';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MutationDefaultsContext } from './query-client';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';

import { type MaybeRefOrGetter, toRef, toValue } from 'vue';
import { isUnreachableError } from '@/utils/is-unreachable-error';

export const DAMAGES_QUERY_KEY = 'damages';

export const useDamage = (id: string) => {
  return useQuery({
    queryKey: [DAMAGES_QUERY_KEY, id],
    queryFn: () => damagesService.getOne(id),
  });
};

export const useDamages = (
  query: MaybeRefOrGetter<FindAllDamagesQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [DAMAGES_QUERY_KEY, toRef(query)],
    queryFn: () => damagesService.getAllWithQuery(toValue(query)),
    enabled: toValue(enabled),
  });
};

export const useUpdateDamage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [DAMAGES_QUERY_KEY],
    mutationFn: ({ id, damage }: { id: string; damage: UpdateDamageDto }) =>
      damagesService.update(id, damage),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Damage updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      updateItemOnQueriesData<Damage>([DAMAGES_QUERY_KEY], result, queryClient);
    },
  });
};

export const useCreateDamage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [DAMAGES_QUERY_KEY],
    mutationFn: (damage: CreateDamageDto) => damagesService.create(damage),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Damage created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      addItemToQueriesData<Damage>([DAMAGES_QUERY_KEY], result, queryClient);
    },
    onError: (error, variables, onMutateResult, context) => {
      // Out of reach, the damage form keeps the Damage on the device instead: nothing failed.
      if (isUnreachableError(error)) return;
      queryClient.getMutationDefaults([])?.onError?.(error, variables, onMutateResult, context);
    },
  });
};

export const useDeleteDamage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [DAMAGES_QUERY_KEY],
    mutationFn: (id: string) => damagesService.remove(id),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Damage removed',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      removeItemFromQueriesData<Damage>([DAMAGES_QUERY_KEY], variables, queryClient);
    },
  });
};
